import {
  cannedWorkTaskGroupPages,
  cannedWorkTasksInfiniteQuery,
} from "@common/api/quoting/canned"
import type {
  CannedWorkTaskDetailPublic,
  QuoteTaskGroupPublic,
  ReviewStatus,
} from "@common/client"
import { UserInitials } from "@common/components/shared/user-initials"
import { Checkbox } from "@common/components/ui/checkbox"
import { GridTable } from "@common/components/ui/grid-table"
import { Skeleton } from "@common/components/ui/skeleton"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useRangeToggle } from "@common/hooks/use-range-toggle"
import { formatShortDate } from "@common/lib/date-utils"
import { cn } from "@common/lib/utils"
import {
  type QueryClient,
  useSuspenseInfiniteQuery,
} from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "@tanstack/react-router"
import { ClipboardList } from "lucide-react"
import { useEffect } from "react"
import { ErpAvatar } from "@/components/layout/erp-avatar"
import { GroupedTable } from "@/components/layout/grouped-table"
import { ListEmpty } from "@/components/layout/list-empty"
import {
  type ListColumns,
  type ListSort,
  ListTableCell,
  ListTableCheckbox,
  ListTableHeader,
  ListTableReveal,
  ListTableRow,
  ListTableSort,
  ListTableStamp,
  ListTableText,
} from "@/components/layout/list-table"
import {
  DeleteMenuItem,
  ListTableMenu,
} from "@/components/layout/list-table-menu"
import { LoadMoreSentinel } from "@/components/layout/load-more-sentinel"
import { useGroupPages } from "@/components/layout/use-group-paging"
import { type ColumnArrange, useColumnLayout } from "@/hooks/use-column-layout"
import type { GroupLabel } from "@/lib/grouping"
import {
  useTaskGroups,
  useTaskGroupsReadable,
} from "@/routes/_layout/quotes_/$quoteId/-lib/task-groups"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  type Column,
  isSortable,
  type Slot,
  slotsOf,
  type TaskSort,
} from "../-lib/columns"
import {
  hasTaskFilters,
  reviewStatusLabel,
  type TaskFilters,
  type TaskGrouping,
  taskDisplay,
  taskGroupings,
  taskGroupParams,
  taskListParams,
  taskSort,
} from "../-lib/filters"
import { chargesPrice, taskApplicabilityLabel } from "../-lib/price"
import { ReviewStatusIcon } from "./review-status-icon"
import { SelectionBar } from "./selection-bar"

const SELECT_ALL_REVEAL =
  "opacity-0 group-hover/header:opacity-100 focus:opacity-100"

const ROW_LINK = cn(
  "col-span-full grid grid-cols-subgrid",
  "text-foreground hover:no-underline",
)

export type Picked = ReadonlySet<string>

type Selection = {
  slots: Slot[]
  sort: ListSort<TaskSort>
  packageId: string | null
  taskId: string | undefined
  picked: Picked
  onPick: (picked: Picked) => void
  onSort: (column: TaskSort) => void
}

type Table = {
  style: React.CSSProperties
  header: React.ReactNode
  renderRows: (
    rows: CannedWorkTaskDetailPublic[],
    order: CannedWorkTaskDetailPublic[],
  ) => React.ReactNode
}

function columnsOf(slots: Slot[]): ListColumns<Slot> {
  return {
    slots,
    end: ["price"],
    muted: ["number", "applies_to", "updated"],
  }
}

function statusLabel(status: ReviewStatus): GroupLabel {
  return {
    key: status,
    label: reviewStatusLabel(status),
    icon: <ReviewStatusIcon status={status} />,
  }
}

function taskGroupLabel(
  task: CannedWorkTaskDetailPublic,
  taskGroups: QuoteTaskGroupPublic[],
): GroupLabel {
  const taskGroup = taskGroups.find(
    (candidate) => candidate.id === task.task_group_id,
  )
  return taskGroup
    ? { key: taskGroup.id, label: taskGroup.name }
    : { key: "", label: "No task group" }
}

export function useTaskGroupings(
  packageId: string | null,
): readonly TaskGrouping[] {
  return taskGroupings(packageId, useTaskGroupsReadable())
}

export function prefetchTasks(
  queryClient: QueryClient,
  filters: TaskFilters,
  packageId: string | null,
  offered: readonly TaskGrouping[],
): Promise<unknown> {
  const { group } = taskDisplay(filters, offered)
  return group
    ? queryClient.ensureQueryData(
        cannedWorkTaskGroupPages.groups(
          taskGroupParams(filters, packageId, group),
        ),
      )
    : queryClient.ensureInfiniteQueryData(
        cannedWorkTasksInfiniteQuery(taskListParams(filters, packageId)),
      )
}

export function TaskTable({
  filters,
  packageId,
  columns,
  picked,
  onPick,
  onSort,
}: {
  filters: TaskFilters
  packageId: string | null
  columns: Column[]
  picked: Picked
  onPick: (picked: Picked) => void
  onSort: (column: TaskSort) => void
}): React.ReactElement {
  const taskId = useParams({
    strict: false,
    select: (params) => params.taskId,
  })
  const canReview = useHasPermission("canned_work_package:review")
  const canArchive = useHasPermission("canned_work_package:delete")
  const selection: Selection = {
    slots: slotsOf(
      columns,
      (canReview && !filters.archived) || canArchive,
      canArchive && packageId === null && !filters.archived,
    ),
    sort: taskSort(filters),
    packageId,
    taskId,
    picked,
    onPick,
    onSort,
  }
  const { group } = taskDisplay(filters, useTaskGroupings(packageId))

  return group ? (
    <GroupedTaskTable
      filters={filters}
      packageId={packageId}
      group={group}
      selection={selection}
    />
  ) : (
    <TaskList filters={filters} packageId={packageId} selection={selection} />
  )
}

function GroupedTaskTable({
  filters,
  packageId,
  group,
  selection,
}: {
  filters: TaskFilters
  packageId: string | null
  group: TaskGrouping
  selection: Selection
}): React.ReactElement {
  const { data, paging } = useGroupPages(
    cannedWorkTaskGroupPages,
    taskGroupParams(filters, packageId, group),
    (task) => task.id,
  )
  const taskGroups = useTaskGroups()
  const groupOf = (
    task: CannedWorkTaskDetailPublic,
    grouping: TaskGrouping,
  ): GroupLabel => {
    switch (grouping) {
      case "task_group":
        return taskGroupLabel(task, taskGroups)
      case "status":
        return statusLabel(task.latest_version.review_status)
    }
  }

  if (data.groups.length === 0) {
    return <NoTasks filters={filters} packageId={packageId} />
  }

  return (
    <SelectableTasks tasks={paging.rows} selection={selection}>
      {(table) => (
        <GroupedTable
          paging={paging}
          style={table.style}
          header={table.header}
          group={group}
          subgroup={undefined}
          groupOf={groupOf}
          renderRows={table.renderRows}
          sticky
        />
      )}
    </SelectableTasks>
  )
}

function TaskList({
  filters,
  packageId,
  selection,
}: {
  filters: TaskFilters
  packageId: string | null
  selection: Selection
}): React.ReactElement {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useSuspenseInfiniteQuery(
    cannedWorkTasksInfiniteQuery(taskListParams(filters, packageId)),
  )
  const tasks = data.pages.flatMap((page) => page.data)

  if (tasks.length === 0) {
    return <NoTasks filters={filters} packageId={packageId} />
  }

  return (
    <SelectableTasks tasks={tasks} selection={selection}>
      {(table) => (
        <>
          <GridTable style={table.style}>
            {table.header}
            {table.renderRows(tasks, tasks)}
          </GridTable>
          <LoadMoreSentinel
            noun="template tasks"
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isError={isFetchNextPageError}
            fetchNextPage={fetchNextPage}
          />
        </>
      )}
    </SelectableTasks>
  )
}

function SelectableTasks({
  tasks,
  selection: { slots, sort, packageId, taskId, picked, onPick, onSort },
  children,
}: {
  tasks: CannedWorkTaskDetailPublic[]
  selection: Selection
  children: (table: Table) => React.ReactNode
}): React.ReactElement {
  const rangeToggle = useRangeToggle<string>()
  const layout = useColumnLayout(slots, COLUMN_SIZING)
  const selectable = slots.includes("select")
  const chosen = selectable ? tasks.filter((task) => picked.has(task.id)) : []
  const hidden = chosen.length !== picked.size
  const pick = (rows: CannedWorkTaskDetailPublic[]) =>
    onPick(new Set(rows.map((task) => task.id)))

  useEffect(() => {
    if (hidden) pick(chosen)
  })
  const toggle = (
    order: CannedWorkTaskDetailPublic[],
    id: string,
    range: boolean,
  ) =>
    onPick(
      rangeToggle(
        order.map((task) => task.id),
        picked,
        id,
        range,
      ),
    )

  return (
    <>
      {children({
        style: { gridTemplateColumns: layout.template },
        header: (
          <TaskHeader
            slots={layout.slots}
            sort={sort}
            arrange={layout.arrange}
            all={tasks.length > 0 && chosen.length === tasks.length}
            some={chosen.length > 0}
            onSort={onSort}
            onAll={(on) => pick(on ? tasks : [])}
          />
        ),
        renderRows: (rows, order) =>
          rows.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              packageId={packageId}
              slots={layout.slots}
              selected={task.id === taskId}
              checked={picked.has(task.id)}
              onCheck={(range) => toggle(order, task.id, range)}
            />
          )),
      })}
      {chosen.length > 0 && (
        <SelectionBar
          tasks={chosen}
          deletable={packageId === null}
          onSelect={(ids) => onPick(new Set(ids))}
        />
      )}
    </>
  )
}

function NoTasks({
  filters,
  packageId,
}: {
  filters: TaskFilters
  packageId: string | null
}): React.ReactElement {
  const filtered = Boolean(filters.search) || hasTaskFilters(filters)
  return (
    <ListEmpty
      icon={ClipboardList}
      title={filtered ? "No matching template tasks" : "No template tasks"}
      body={emptyBody(filtered, packageId)}
    />
  )
}

function emptyBody(filtered: boolean, packageId: string | null): string {
  if (filtered) return "Try a different search or filter."
  return packageId
    ? "Use New template task to create one in this template quote."
    : "Create a template task to reuse its labor, parts, services and steps on quotes."
}

function TaskHeader({
  slots,
  sort,
  arrange,
  all,
  some,
  onSort,
  onAll,
}: {
  slots: Slot[]
  sort: ListSort<TaskSort>
  arrange: ColumnArrange<Slot>
  all: boolean
  some: boolean
  onSort: (column: TaskSort) => void
  onAll: (on: boolean) => void
}): React.ReactElement {
  return (
    <ListTableHeader columns={columnsOf(slots)} sort={sort} arrange={arrange}>
      {(slot) =>
        slot === "select" ? (
          <Checkbox
            aria-label="Select all template tasks"
            checked={all}
            className={cn(!some && SELECT_ALL_REVEAL)}
            onCheckedChange={(next) => onAll(next === true)}
          />
        ) : (
          <HeadLabel slot={slot} sort={sort} onSort={onSort} />
        )
      }
    </ListTableHeader>
  )
}

function HeadLabel({
  slot,
  sort,
  onSort,
}: {
  slot: Slot
  sort: ListSort<TaskSort>
  onSort: (column: TaskSort) => void
}): React.ReactNode {
  if (slot === "select" || slot === "status" || slot === "actions") return null
  return isSortable(slot) ? (
    <ListTableSort
      column={slot}
      label={COLUMN_LABELS[slot]}
      sort={sort}
      onSort={onSort}
    />
  ) : (
    <ListTableText>{COLUMN_LABELS[slot]}</ListTableText>
  )
}

function TaskRow({
  task,
  packageId,
  slots,
  selected,
  checked,
  onCheck,
}: {
  task: CannedWorkTaskDetailPublic
  packageId: string | null
  slots: Slot[]
  selected: boolean
  checked: boolean
  onCheck: (range: boolean) => void
}): React.ReactElement {
  const columns = columnsOf(slots)
  const cells = slots.map((slot) =>
    slot === "actions" ? (
      <ListTableCell
        key={slot}
        columns={columns}
        slot={slot}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
        }}
      >
        <TaskActions task={task} />
      </ListTableCell>
    ) : slot === "select" ? (
      <ListTableCell
        key={slot}
        columns={columns}
        slot={slot}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
        }}
      >
        <ListTableCheckbox
          label={`Select ${task.name}`}
          checked={checked}
          onToggle={onCheck}
        />
      </ListTableCell>
    ) : (
      <ListTableCell key={slot} columns={columns} slot={slot}>
        <SlotValue slot={slot} task={task} />
      </ListTableCell>
    ),
  )

  return (
    <ListTableRow
      aria-selected={selected}
      selected={selected || checked}
      dimmed={Boolean(task.deleted_at)}
    >
      {packageId ? (
        <Link
          to="/templates/quotes/$packageId/tasks/$taskId"
          params={{ packageId, taskId: task.id }}
          search={true}
          className={ROW_LINK}
        >
          {cells}
        </Link>
      ) : (
        <Link
          to="/templates/tasks/$taskId"
          params={{ taskId: task.id }}
          search={true}
          className={ROW_LINK}
        >
          {cells}
        </Link>
      )}
    </ListTableRow>
  )
}

function SlotValue({
  slot,
  task,
}: {
  slot: Slot
  task: CannedWorkTaskDetailPublic
}): React.ReactNode {
  const version = task.latest_version
  switch (slot) {
    case "select":
    case "actions":
      return null
    case "status":
      return <ReviewStatusIcon status={version.review_status} />
    case "number":
      return <ListTableText>{task.number}</ListTableText>
    case "name":
      return <ListTableText>{task.name}</ListTableText>
    case "applies_to":
      return <ListTableText>{taskApplicabilityLabel(task)}</ListTableText>
    case "price":
      return <ListTableText>{chargesPrice(version.charges)}</ListTableText>
    case "updated":
      return <Updated task={task} />
  }
}

function TaskActions({
  task,
}: {
  task: CannedWorkTaskDetailPublic
}): React.ReactElement {
  const navigate = useNavigate()

  return (
    <ListTableReveal>
      <ListTableMenu label={task.name}>
        <DeleteMenuItem
          onSelect={() =>
            navigate({
              from: "/templates/tasks",
              to: "/templates/tasks/delete",
              search: (previous) => ({ ...previous, ids: [task.id] }),
            })
          }
        />
      </ListTableMenu>
    </ListTableReveal>
  )
}

function Updated({
  task,
}: {
  task: CannedWorkTaskDetailPublic
}): React.ReactElement {
  const version = task.latest_version
  const updater = version.last_modified_by ?? task.created_by
  const synced =
    task.source === "erp_sync" && task.previous_versions.length === 0
  const avatar = synced ? (
    <ErpAvatar />
  ) : (
    updater && <UserInitials user={updater} />
  )
  return (
    <ListTableStamp avatar={avatar}>
      {formatShortDate(version.updated_at)}
    </ListTableStamp>
  )
}

export function TableSkeleton(): React.ReactElement {
  return (
    <div className="space-y-3 p-4">
      {[1, 2, 3, 4, 5].map((index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  )
}
